// Visual breakdown of CRM contacts vs editable targets.
// Mounted at the top of /admin/crm to give an at-a-glance pipeline view.
import { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { CATEGORY_LABELS } from "@/pages/admin/AdminCRMPage";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const STATUS_LABEL = {
  lead: "Nouveau", contacted: "Contacté", qualified: "Qualifié",
  customer: "Client", lost: "Perdu",
};

// Brand palette — pink/blue accents with neutral support tones
const PALETTE = [
  "#ce1a5f", "#2e5067", "#f59e0b", "#10b981", "#8b5cf6",
  "#ef4444", "#06b6d4", "#84cc16", "#f97316", "#64748b",
];

function GoalInput({ value, onCommit, testId }) {
  // Uncontrolled input — remounts on external `value` change via the `key`
  // prop so the field always shows the latest persisted target without a
  // useEffect-based sync.
  return (
    <input
      key={String(value ?? 0)}
      type="number" min="0" inputMode="numeric"
      defaultValue={String(value ?? 0)}
      onBlur={(e) => {
        const n = Math.max(0, parseInt(e.target.value || "0", 10) || 0);
        if (n !== Number(value)) onCommit(n);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-16 text-right bg-transparent border-b border-dashed border-[color:var(--gv-line)] focus:border-[color:var(--gv-pink)] outline-none text-xs tabular-nums"
      data-testid={testId}
    />
  );
}

function ProgressRow({ label, count, target, onTarget, testId }) {
  const pct = target > 0 ? Math.min(100, Math.round((count / target) * 100)) : 0;
  const color = pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-[color:var(--gv-pink)]";
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-xs text-[color:var(--gv-ink)]/80">
        <span className="truncate">{label}</span>
        <span className="tabular-nums">
          <strong className="text-[color:var(--gv-ink)]">{count}</strong>
          <span className="text-[color:var(--gv-ink)]/40"> / </span>
          <GoalInput value={target} onCommit={onTarget} testId={`${testId}-goal`} />
        </span>
      </div>
      <div className="h-2 rounded-full bg-[color:var(--gv-line)]/60 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const CrmDashboard = () => {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (p = period) => {
    setLoading(true);
    try {
      const [bd, g] = await Promise.all([adminApi.statsBreakdown(p), adminApi.getCrmGoals()]);
      setData(bd);
      setGoals(g);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { refresh(period); }, [period]);

  const pieStatus = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_status || {})
      .map(([k, v]) => ({ name: STATUS_LABEL[k] || k, key: k, value: v }))
      .filter((x) => x.value > 0);
  }, [data]);

  const pieCategory = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_category || {})
      .map(([k, v]) => ({ name: CATEGORY_LABELS[k] || k, key: k, value: v }))
      .filter((x) => x.value > 0);
  }, [data]);

  const updateGoal = async (scope, key, period_, val) => {
    // Optimistic update
    const next = { ...goals };
    if (scope === "global") {
      next.global = { ...(next.global || {}), [period_]: val };
    } else {
      next[scope] = { ...(next[scope] || {}), [key]: { ...((next[scope] || {})[key] || {}), [period_]: val } };
    }
    setGoals(next);
    const payload = scope === "global"
      ? { global: { [period_]: val } }
      : { [scope]: { [key]: { [period_]: val } } };
    try {
      const saved = await adminApi.updateCrmGoals(payload);
      setGoals(saved);
    } catch {
      // Rollback by refetching
      const g = await adminApi.getCrmGoals();
      setGoals(g);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 mb-6 text-sm text-[color:var(--gv-ink)]/60" data-testid="crm-dashboard-loading">
        Chargement du dashboard…
      </div>
    );
  }
  if (!data || !goals) return null;

  const globalTarget = Number(goals.global?.[period] || 0);
  const globalCount = Number(data.total || 0);
  const globalPct = globalTarget > 0 ? Math.min(100, Math.round((globalCount / globalTarget) * 100)) : 0;

  return (
    <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 md:p-6 mb-6" data-testid="crm-dashboard">
      {/* Header + period toggle */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Pipeline & objectifs</h2>
          <p className="text-xs text-[color:var(--gv-ink)]/60">
            Contacts {period === "month" ? "ce mois-ci" : period === "year" ? "cette année" : "depuis le début"} · cliquez sur un chiffre objectif pour l&apos;éditer
          </p>
        </div>
        <div className="inline-flex rounded-[3px] border border-[color:var(--gv-line)] overflow-hidden text-xs" data-testid="crm-dashboard-period">
          {[
            { k: "month", label: "Mois" },
            { k: "year", label: "Année" },
            { k: "all", label: "Total" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setPeriod(opt.k)}
              className={`px-3 py-1.5 transition ${period === opt.k ? "bg-[color:var(--gv-ink)] text-white" : "bg-white text-[color:var(--gv-ink)]/70 hover:bg-[color:var(--gv-mist)]"}`}
              data-testid={`crm-dashboard-period-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Global KPI */}
      <div className="mb-6 p-4 rounded-[4px] bg-gradient-to-r from-[color:var(--gv-pink)]/8 to-[color:var(--gv-blue)]/8 border border-[color:var(--gv-line)]" data-testid="crm-dashboard-global">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60">Objectif global ({period === "all" ? "cumul" : period === "month" ? "mois" : "année"})</p>
            <p className="text-3xl font-display text-[color:var(--gv-ink)] tabular-nums">
              {globalCount}
              <span className="text-[color:var(--gv-ink)]/30 text-xl"> / </span>
              {period !== "all" ? (
                <GoalInput
                  value={globalTarget}
                  onCommit={(v) => updateGoal("global", null, period, v)}
                  testId="crm-dashboard-global-goal"
                />
              ) : (
                <span className="text-[color:var(--gv-ink)]/40 text-xl">—</span>
              )}
            </p>
          </div>
          {period !== "all" && (
            <div className="text-right">
              <p className="text-2xl font-display text-[color:var(--gv-pink)] tabular-nums">{globalPct}%</p>
              <p className="text-[11px] text-[color:var(--gv-ink)]/60">{globalTarget - globalCount > 0 ? `Encore ${globalTarget - globalCount} à atteindre` : "Objectif atteint 🎉"}</p>
            </div>
          )}
        </div>
        {period !== "all" && (
          <div className="h-2.5 rounded-full bg-white/70 overflow-hidden">
            <div className="h-full bg-[color:var(--gv-pink)] transition-all" style={{ width: `${globalPct}%` }} />
          </div>
        )}
      </div>

      {/* Three columns: status / category / country */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* By status */}
        <div data-testid="crm-dashboard-status">
          <h3 className="text-sm font-semibold text-[color:var(--gv-ink)] mb-3">Par statut</h3>
          <div className="h-40 mb-3">
            {pieStatus.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-[color:var(--gv-ink)]/40">Aucun contact sur la période</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieStatus} dataKey="value" nameKey="name" innerRadius={36} outerRadius={62} paddingAngle={2}>
                    {pieStatus.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-2">
            {["lead", "contacted", "qualified", "customer", "lost"].map((s) => (
              <ProgressRow
                key={s}
                label={STATUS_LABEL[s]}
                count={Number(data.by_status?.[s] || 0)}
                target={Number(goals.by_status?.[s]?.[period === "all" ? "year" : period] || 0)}
                onTarget={(v) => updateGoal("by_status", s, period === "all" ? "year" : period, v)}
                testId={`crm-dashboard-status-${s}`}
              />
            ))}
          </div>
        </div>

        {/* By category */}
        <div data-testid="crm-dashboard-category">
          <h3 className="text-sm font-semibold text-[color:var(--gv-ink)] mb-3">Par catégorie</h3>
          <div className="h-40 mb-3">
            {pieCategory.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-[color:var(--gv-ink)]/40">Aucun contact sur la période</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieCategory} dataKey="value" nameKey="name" innerRadius={36} outerRadius={62} paddingAngle={2}>
                    {pieCategory.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {Object.keys(CATEGORY_LABELS).map((c) => (
              <ProgressRow
                key={c}
                label={CATEGORY_LABELS[c]}
                count={Number(data.by_category?.[c] || 0)}
                target={Number(goals.by_category?.[c]?.[period === "all" ? "year" : period] || 0)}
                onTarget={(v) => updateGoal("by_category", c, period === "all" ? "year" : period, v)}
                testId={`crm-dashboard-category-${c}`}
              />
            ))}
          </div>
        </div>

        {/* By country (top 10 horizontal bar) */}
        <div data-testid="crm-dashboard-country">
          <h3 className="text-sm font-semibold text-[color:var(--gv-ink)] mb-3">Top 10 pays</h3>
          {(data.by_country || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs text-[color:var(--gv-ink)]/40">Aucun pays renseigné</div>
          ) : (
            <div style={{ height: Math.max(180, (data.by_country.length * 28) + 20) }}>
              <ResponsiveContainer>
                <BarChart data={data.by_country} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip cursor={{ fill: "rgba(206,26,95,0.06)" }} />
                  <Bar dataKey="count" fill="#2e5067" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CrmDashboard;
